'
<template>
  <div>
    <h3 class="title is-6 mb-10">{{ detail.plan_name || $t('docflow.documentPlan') }}</h3>
    <div class="controls" v-if="editable">
      <button @click="setTitle" class="button is-success mr-10">{{ $t('docflow.setPlanTitle') }}</button>
      <button @click="createItem" class="button is-success mr-10">{{ $t('docflow.createPlanItem') }}</button>
      <button @click="createGroup" class="button mr-10">{{ $t('docflow.createPlanGroup') }}</button>
    </div>
    <table class="report-table mt-20">
      <thead>
      <tr>
        <td width="40" class="has-text-centered">№</td>
        <td>{{ $t('interface.description') }}</td>
        <td width="200">{{ $t('docflow.realisation_mechanisms') }}</td>
        <td width="200">{{ $t('documents.executor') }}</td>
        <td width="200">{{ $t('docflow.plan.executors') }}</td>
        <td width="100">{{ $t('docflow.fields.term_date') }}</td>
        <td width="130" class="has-text-centered" v-if="editable || isChangeRequest">{{ $t('tasks.actions') }}</td>
      </tr>
      </thead>
      <tbody v-if="(detail.plan_groups && detail.plan_groups.length)
                    || (detail.plan_items && detail.plan_items.length)">
      <template v-for="group in detail.plan_groups" v-if="group.items.length > 0">
        <tr>
          <td colspan="1" class="has-text-centered">
            {{ group.order_number }}
          </td>
          <td colspan="5" class="has-text-centered">
            <span class="group-name has-text-weight-bold">
              {{ group.name }}
            </span>
          </td>
          <td v-if="editable">
            <a class="button is-small ml-10" @click="editGroup(group.id)">
              <b-icon icon="pencil" size="is-small"></b-icon>
            </a>
            <a class="button is-small is-danger ml-10" @click="removeGroup(group.id)">
              <b-icon icon="delete" size="is-small"></b-icon>
            </a>
          </td>
        </tr>
        <tr v-for="item in group.items">
          <td class="has-text-centered">{{ group.order_number }}.{{ item.order_number }}</td>
          <td>
            <span class="group-name">
              {{ item.name }}
            </span>
          </td>
          <td>
            <span class="group-name">
              {{ item.realisation_mechanisms }}
            </span>
          </td>
          <td>
            <template v-if="item.maintainer">{{ item.maintainer.name }}</template>
            <template v-else>&ndash;</template>
          </td>
          <td>
            <template v-for="executor in item.executors">
              <div class="mb-10">{{ executor.name }}</div>
            </template>
            <button class="button is-small" @click="addExecutors(item.id)" v-if="editable">
              {{ $t('docflow.noPlanExecutors') }}
            </button>
          </td>
          <td>{{ item.term_date | localedDateWithYear }}</td>
          <td v-if="editable || isChangeRequest">
            <a class="button is-small ml-10"
               @click="editItem(item.id)">
              <b-icon icon="pencil" size="is-small"></b-icon>
            </a>
            <a class="button is-small is-danger ml-10"
               v-if="item.parent === null && !isChangeRequest && editable"
               @click="removeItem(item.id)">
              <b-icon icon="delete" size="is-small"></b-icon>
            </a>
          </td>
        </tr>
      </template>
      <tr v-for="item in detail.plan_items">
        <td class="has-text-centered">{{ item.order_number }}</td>
        <td>
          <span class="group-name">
              {{ item.name }}
          </span>
        </td>
        <td>
            <span class="group-name">
              {{ item.realisation_mechanisms }}
            </span>
        </td>
        <td>
          <template v-if="item.maintainer">{{ item.maintainer.name }}</template>
          <template v-else>&ndash;</template>
        </td>
        <td>
          <template v-if="item.executors.length > 0"
                    v-for="executor in item.executors">
            <div class="mb-10">{{ executor.name }}</div>
          </template>
          <button class="button is-small" @click="addExecutors(item.id)" v-if="editable">
            {{ $t('docflow.noPlanExecutors') }}
          </button>
        </td>
        <td>{{ item.term_date | localedDateWithYear }}</td>
        <td v-if="editable || isChangeRequest">
          <a class="button is-small ml-10"
             @click="editItem(item.id)">
            <b-icon icon="pencil" size="is-small"></b-icon>
          </a>
          <a class="button is-small is-danger ml-10"
             v-if="item.parent === null && !isChangeRequest && editable"
             @click="removeItem(item.id)">
            <b-icon icon="delete" size="is-small"></b-icon>
          </a>
        </td>
      </tr>
      </tbody>
      <tbody v-else>
      <tr>
        <td colspan="7">
          <section class="section has-text-grey-light has-text-centered">
            {{ $t('docflow.noPlanItemsExist') }}
          </section>
        </td>
      </tr>
      </tbody>
    </table>
    <b-modal class="dialog" :active.sync="isNoteModalActive" has-modal-card>
      <div class="modal-card" style="width: auto">
        <section class="modal-card-body is-titleless">
          <textarea class="textarea"
                    v-model="note"
                    :placeholder="$t('docflow.planTitlePlaceholder')">
          </textarea>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-primary" @click="noteSubmit">OK</button>
          <button class="button is-danger" type="button" @click="isNoteModalActive = false">
            {{ $t('interface.cancel') }}
          </button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import {mapState, mapGetters} from 'vuex'

  export default {
    props: {
      edit: {
        type: Boolean,
        default: true
      },
      type: {
        type: String,
        default: 'default'
      }
    },
    data() {
      return {
        isNoteModalActive: false,
        note: ''
      }
    },
    methods: {
      setTitle() {
        this.isNoteModalActive = true
      },
      noteSubmit() {
        this.$store.dispatch('Docflow/update', {
          id: this.detail.id,
          category: this.type === 'default' ? 'outgoing' : 'incoming',
          plan_name: this.note
        }).then(() => this.$emit('refresh'))
        this.isNoteModalActive = false
        this.note = ''
      },
      createGroup() {
        this.$router.push({
          name: 'createPlanGroup',
          params: {
            documentId: this.detail.id
          }
        })
      },
      editGroup(groupId) {
        this.$router.push({
          name: 'editPlanGroup',
          params: {
            documentId: this.detail.id,
            groupId
          }
        })
      },
      addExecutors(itemId) {
        this.$router.push({
          name: 'editPlanExecutors',
          params: {
            documentId: this.detail.id,
            itemId
          }
        })
      },
      createItem() {
        this.$router.push({
          name: 'createPlanItem',
          params: {
            documentId: this.detail.id
          }
        })
      },
      editItem(itemId) {
        this.$router.push({
          name: 'editPlanItem',
          params: {
            documentId: this.detail.id,
            itemId
          }
        })
      },
      removeItem(itemId) {
        this.$dialog.confirm({
          title: this.$t('docflow.sureRemove'),
          message: this.$t('docflow.removePlanItem'),
          confirmText: 'OK',
          cancelText: this.$t('interface.cancel'),
          type: 'is-danger',
          hasIcon: true,
          onConfirm: () => {
            this.$store.dispatch('Docflow/removePlanItem', itemId).then(() => {
              this.$store.dispatch('Docflow/getDetail', {
                documentId: this.$route.params.documentId,
                category: 'outgoing'
              })
            })
          }
        })
      },
      removeGroup(groupId) {
        this.$dialog.confirm({
          title: this.$t('docflow.sureRemove'),
          message: this.$t('docflow.removePlanGroup'),
          confirmText: 'OK',
          cancelText: this.$t('interface.cancel'),
          type: 'is-danger',
          hasIcon: true,
          onConfirm: () => {
            this.$store.dispatch('Docflow/removePlanGroup', groupId).then(() => {
              this.$store.dispatch('Docflow/getDetail', {
                documentId: this.$route.params.documentId,
                category: 'outgoing'
              })
            })
          }
        })
      },
    },
    computed: {
      ...mapState('Docflow', ['detail']),
      ...mapState('Account', ['account']),
      ...mapGetters('Account', ['isDocflowSupervisor']),
      editable() {
        if (this.edit && this.type === 'default') {
          return ['new', 'rejected'].includes(this.detail.state)
            && (this.detail.maintainer.id === this.account.employee.id
              || this.detail.author.id === this.account.employee.id)
        } else return this.edit && this.type === 'incoming'
      },
      isChangeRequest() {
        return this.detail.change_state === 'approved'
          && (this.detail.supervisor.id === this.account.employee.id || this.isDocflowSupervisor)
      }
    }
  }
</script>

<style lang="scss" scoped>
  table .button {
    vertical-align: sub;
  }

  .group-name {
    vertical-align: middle;
  }
</style>
